import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
    RiHome3Line,
    RiClipboardLine,
    RiUserLine,
    RiBriefcaseLine,
    RiWallet3Line,
    RiMessage3Line,
    RiSettings3Line,
} from 'react-icons/ri'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'

import routes from '@/routes/routes'
import { useProfileStatus } from '@/hooks/useProfile'
import { mainLogo, projectName } from "@/utils/constants/common"
import useAuthStore, { type UserDetails } from '@/stores/AuthStore'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'

import './Navbar.css'

const Navbar = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('common')
    const location = useLocation()

    const { isLogged, getUserDetails } = useAuthStore()
    const { data: profileStatus } = useProfileStatus()
    
    // const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(undefined)
    const [profileLoading, setProfileLoading] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    

    useEffect(() => {
        if (isLogged) {
            setProfileLoading(true)
            getUserDetails().then(userData => {
                setUserDetails(userData)
                setProfileLoading(false)
            })
        }
    }, [isLogged, getUserDetails])
    
    // Handle scroll event to add glass effect when scrolled
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true)
            } else {
                setScrolled(false)
            }
        }
        
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const isActive = (path: string) => {
        if (path === routes.serviceSearch) {
            return location.pathname === routes.serviceSearch || location.pathname === routes.home
        }
        return location.pathname.startsWith(path)
    }

    // Desktop navigation items (shown in top navbar)
    const desktopNavItems = (() => {
        if (!isLogged) {
            return [
                { path: routes.serviceSearch, icon: RiHome3Line, label: 'Servicios' },
            ]
        }
        if (profileStatus?.is_provider) {
            return [
                { path: routes.providerRequests, icon: RiClipboardLine, label: 'Solicitudes' },
                { path: routes.providerServices, icon: RiBriefcaseLine, label: 'Mis Servicios' },
                { path: routes.providerWallet, icon: RiWallet3Line, label: 'Billetera' },
                { path: routes.conversations, icon: RiMessage3Line, label: 'Chats' },
                { path: routes.providerSetup, icon: RiSettings3Line, label: 'Configuración' },
            ]
        }
        return [
            { path: routes.serviceSearch, icon: RiHome3Line, label: 'Servicios' },
            { path: routes.requests, icon: RiClipboardLine, label: 'Mis Pedidos' },
            
            // { path: routes.conversations, icon: RiMessage3Line, label: 'Chats' },
        ]
    })()

    // Mobile bottom bar items (different from desktop)
    const mobileBottomItems = (() => {
        if (!isLogged) {
            return [
                { path: routes.serviceSearch, icon: RiHome3Line, label: 'Servicios' },
                { path: routes.auth, icon: RiUserLine, label: 'Ingresar' },
            ]
        }
        if (profileStatus?.is_provider) {
            return [
                { path: routes.providerSetup, icon: RiSettings3Line, label: 'Config' },
                { path: routes.providerRequests, icon: RiClipboardLine, label: 'Solicitudes' },
                { path: routes.providerServices, icon: RiBriefcaseLine, label: 'Servicios' },
                { path: routes.providerWallet, icon: RiWallet3Line, label: 'Billetera' },
                { path: routes.profile, icon: RiUserLine, label: 'Perfil' },
            ]
        }
        return [
            { path: routes.requests, icon: RiClipboardLine, label: 'Pedidos' },
            { path: routes.serviceSearch, icon: RiHome3Line, label: 'Servicios' },
            // { path: routes.conversations, icon: RiMessage3Line, label: 'Chats' },
            { path: routes.profile, icon: RiUserLine, label: 'Perfil' },
        ]
    })()

    return (
        <>
            {/* DESKTOP: Top Navbar */}
            <nav className={`navbar-desktop ${scrolled ? 'navbar-scrolled' : ''}`}>
                {/* Main logo */}
                <Link to={routes.main} className="navbar-logo">
                    <img src={mainLogo.src} alt={mainLogo.alt} className="navbar-logo-img" />
                    <span className="navbar-logo-text">{projectName}</span>
                </Link>
                
                {/* Desktop nav links */}
                <div className="navbar-nav-links">
                    {desktopNavItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`navbar-nav-link ${isActive(item.path) ? 'active' : ''}`}
                        >
                            <item.icon className="navbar-icon" />
                            <span className="navbar-link-text">{item.label}</span>
                        </Link>
                    ))}
                </div>
                
                {/* User actions */}
                <div className="navbar-actions">
                    {profileLoading ? (
                        <div className="navbar-user-info fade-in">
                            <Spinner className="navbar-user" />
                        </div>
                    ) : isLogged ? (
                        <div className="navbar-user-info fade-in">
                            <Link to="/profile" className="navbar-user">
                                <div className="navbar-user-avatar">
                                    <span className="avatar-text">{userDetails?.username?.charAt(0).toUpperCase() || 'U'}</span>
                                </div>
                            </Link>
                        </div>
                    ) : (
                        <div className="navbar-auth-actions fade-in">
                            <Button 
                                variant="primary"
                                size="sm"
                                onClick={() => navigate(routes.auth)}
                            >
                                {t('nav_login')}
                            </Button>
                        </div>
                    )}
                </div>
            </nav>

            {/* MOBILE: Bottom Navigation Bar */}
            <nav className="navbar-mobile-bottom" data-provider={profileStatus?.is_provider || false}>
                {mobileBottomItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`mobile-bottom-tab ${isActive(item.path) ? 'active' : ''}`}
                    >
                        <div className="mobile-bottom-tab-content">
                            <item.icon className="mobile-bottom-icon" />
                            <span className="mobile-bottom-label">{item.label}</span>
                        </div>
                    </Link>
                ))}
            </nav>
        </>
    )
}

export default Navbar